import React from 'react';
import SafeSEO from '@/components/SafeSEO';
import { motion } from 'framer-motion';
import { Building2, Zap, Factory, Plane, Droplet, Wifi, Truck } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const Sectors = () => {
  const { t } = useLanguage();

  const sectors = [
    {
      icon: Zap,
      title: t('sectors.items.energy.title'),
      description: t('sectors.items.energy.desc'),
      image: '/images/sector-energy.png',
    },
    {
      icon: Building2,
      title: t('sectors.items.realEstate.title'),
      description: t('sectors.items.realEstate.desc'),
      image: '/images/sector-realestate.png',
    },
    {
      icon: Truck,
      title: t('sectors.items.infra.title'),
      description: t('sectors.items.infra.desc'),
      image: '/images/sector-infra.png',
    },
    {
      icon: Plane,
      title: t('sectors.items.tourism.title'),
      description: t('sectors.items.tourism.desc'),
      image: '/images/sector-tourism.png',
    },
    {
      icon: Droplet,
      title: t('sectors.items.water.title'),
      description: t('sectors.items.water.desc'),
      image: '/images/sector-water.png',
    },
    {
      icon: Wifi,
      title: t('sectors.items.tech.title'),
      description: t('sectors.items.tech.desc'),
      image: '/images/sector-tech.png',
    },
  ];

  return (
    <>
      <SafeSEO
        title={t('sectors.title')}
        description={t('sectors.subtitle')}
        url="https://consulogic-gt.com/sectores"
      />

      <div className="pt-20">
        <section className="relative py-24 bg-gradient-to-br from-[#0A5F7F] to-[#0D7FA3] text-white overflow-hidden">
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/10 rounded-full -ml-48 -mb-48 blur-3xl" />
          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-3xl mx-auto text-center"
            >
              <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight">{t('sectors.title')}</h1>
              <p className="text-xl md:text-2xl text-white/90 leading-relaxed font-light">
                {t('sectors.subtitle')}
              </p>
            </motion.div>
          </div>
        </section>

        <section className="py-24 bg-white">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-20"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-[#0A5F7F] mb-6">
                {t('sectors.experienceTitle')}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-medium">
                {t('sectors.experienceDesc')}
              </p>
            </motion.div>

            <div className="grid grid-cols-1 gap-24">
              {sectors.map((sector, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 !== 0 ? 'lg:flex-row-reverse' : ''}`}
                >
                  <div className={`${index % 2 !== 0 ? 'lg:order-2' : ''} p-10 bg-white/40 backdrop-blur-xl rounded-[2.5rem] border border-gray-100 shadow-xl shadow-gray-200/50 hover:shadow-2xl transition-all duration-500`}>
                    <div className="w-20 h-20 bg-gradient-to-br from-[#0A5F7F] to-[#0D7FA3] rounded-2xl flex items-center justify-center mb-8 shadow-xl shadow-[#0A5F7F]/20">
                      <sector.icon className="h-10 w-10 text-white" />
                    </div>
                    <h3 className="text-3xl font-bold text-[#0A5F7F] mb-6 tracking-tight">{sector.title}</h3>
                    <p className="text-lg text-gray-600 mb-6 leading-relaxed font-medium">{sector.description}</p>
                  </div>
                  <div className={`${index % 2 !== 0 ? 'lg:order-1' : ''} relative group`}>
                    <div className="absolute -inset-4 bg-gradient-to-r from-[#0A5F7F]/10 to-[#0D7FA3]/10 rounded-[3rem] blur-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
                    <img
                      className="w-full h-[450px] object-cover rounded-[2.5rem] shadow-2xl relative z-10 border border-white/20 transition-transform duration-700 group-hover:scale-[1.02]"
                      alt={sector.title}
                      src={sector.image} 
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-24 bg-gradient-to-br from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="max-w-4xl mx-auto text-center bg-white p-12 lg:p-16 rounded-[3rem] border border-gray-100 shadow-2xl"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-[#0A5F7F] mb-8">
                {t('sectors.integratedTitle')}
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed mb-6 font-medium">
                {t('sectors.integratedDesc1')}
              </p>
              <p className="text-xl text-gray-600 leading-relaxed font-medium italic">
                {t('sectors.integratedDesc2')}
              </p>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Sectors;